/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import {
	ArchivedVersionTable,
	ArticleComponentsSection,
	ArticleContainer,
	CosmosComponentContextProps,
	getHero,
	heroOwnsHeading,
	GetLatestCard,
	getSections,
	SectionTypename,
	useCosmosComponentContext,
} from "@cosmos/components";
import {
	CosmosEnumOperatingSystems,
	CosmosPluginDownloadDetailFragment,
	CosmosPluginDownloadFragment,
	PagePluginDetailFragment,
} from "@cosmos/hygraph-schema";
import { useAuth } from "@cosmos/next-hooks";
import { useMemo, useState } from "react";

import { css } from "@panda/css";

import { TemplateProviders } from "../templates";

import type { CleanedBlobRes } from "@cosmos/common";

interface CosmosPageProps {
	pageData?: PagePluginDetailFragment;
	componentContext?: CosmosComponentContextProps;
	eulaAccepted: boolean;
	eulaEnforcementUrl?: string | null;
	blobs?: CleanedBlobRes[];
	/**
	 * `?mode=game` / `?mode=iosgame` — an in-game or console webview. Neutralises
	 * links in the article body, drops article components carrying
	 * non-allowlisted links, and hides `refSections` entirely. Same behaviour as
	 * blog detail; see docs/game-mode.md.
	 */
	isGameMode?: boolean;
}

type GetLatestTopDownloads = {
	downloads?: CosmosPluginDownloadDetailFragment[];
	downloadName?: string;
};

const sectionExtraProps = (__typename: SectionTypename): any => {
	return __typename === "CosmosSectionContentLargeImage" ? { enableAnimate: false } : {};
};

export const CosmosPagePluginDetail = ({
	pageData,
	componentContext,
	eulaEnforcementUrl,
	eulaAccepted,
	blobs,
	isGameMode,
}: CosmosPageProps): React.JSX.Element => {
	const contextFromProvider = useCosmosComponentContext();
	const ctx = componentContext ?? contextFromProvider;
	useAuth(!ctx.enableAuth);

	const [osName, setOsName] = useState<string>();

	// refSections are a separate component tree that never receives the game-mode
	// flag, so their links can't be neutralised. Rather than ship live links into
	// a webview with no nav, they're dropped wholesale in game mode — including
	// the spacers that only exist to separate them. See docs/game-mode.md.
	const showRefSections = !isGameMode && !!pageData?.refSections?.length;

	const cleanedBlobs = useMemo(() => blobs?.filter((blob) => !!blob?.downloadUrl) || [], [blobs]);
	//organize the blobs by latest version and pluck any blobs that match the version
	const blobDownloads = useMemo(() => {
		if (!cleanedBlobs?.length) return [];

		const first = cleanedBlobs[0];
		const latestSemver = first.semver;
		const result: CosmosPluginDownloadFragment[] = [];

		for (let i = 0; i < cleanedBlobs.length; i++) {
			const blob = cleanedBlobs[i];
			const operatingSystem = (blob.operatingSystem || "windows") as CosmosEnumOperatingSystems;
			const title = blob.name;
			const downloadUrl = blob?.downloadUrl;
			const semver = blob?.semver;

			if (i === 0 || semver !== latestSemver) {
				result.push({
					title,
					details: [
						{
							operatingSystem,
							downloadUrl,
							__typename: "CosmosElementPluginDownloadDetail",
						},
					],
				});
				continue;
			}
		}

		return result;
	}, [cleanedBlobs]);

	const { downloads, lastestDownloads } = useMemo((): {
		downloads: CosmosPluginDownloadFragment[];
		lastestDownloads: GetLatestTopDownloads;
	} => {
		const result = blobDownloads?.length ? blobDownloads : pageData?.downloads || [];
		const lastestDownloads: GetLatestTopDownloads = { downloads: [], downloadName: pageData?.hero?.title?.text || "" };

		for (let i = 0; i < result.length; i++) {
			const item = result[i];
			if (!item.details.length) continue;
			for (const detail of item.details) {
				const operatingSystem = (detail.operatingSystem || "windows") as CosmosEnumOperatingSystems;
				const downloadUrl = detail.downloadUrl;
				if (!downloadUrl) continue;
				if (!lastestDownloads?.downloads) lastestDownloads.downloads = [];
				const osExists = lastestDownloads.downloads?.filter(
					(detail) => detail.operatingSystem === operatingSystem,
				)?.length;
				if (osExists) continue;

				lastestDownloads.downloads.push({
					operatingSystem,
					downloadUrl,
					__typename: "CosmosElementPluginDownloadDetail",
				});
			}
		}

		return { downloads: result, lastestDownloads };
	}, [blobDownloads, pageData?.downloads, pageData?.hero?.title?.text]);

	const overrideDownloadUrl = eulaEnforcementUrl && !eulaAccepted ? eulaEnforcementUrl : null;

	// Only a hero that renders an h1 owns it; a decorative hero (e.g. /plugins' glow with no
	// title) must not suppress the first section's promotion — gate on heroOwnsHeading, not
	// presence. Without this the first ref-section (PluginListTable "Plugins") stays h2 → MISSING_H1.
	const hero = getHero(pageData?.hero);

	return (
		<TemplateProviders componentContext={componentContext} isGameMode={isGameMode}>
			{hero}
			<div className={css({ paddingBlockStart: "eds.dimension.32" })}>
				<ArticleContainer>
					<GetLatestCard
						sections={pageData?.latestCardSections}
						downloads={lastestDownloads.downloads}
						downloadName={lastestDownloads.downloadName}
						overrideDownloadUrl={overrideDownloadUrl}
						osName={osName}
						onOsNameChange={setOsName}
					/>
				</ArticleContainer>
				<ArticleComponentsSection articleComponents={pageData?.articleComponents} isGameMode={isGameMode} />
				{pageData?.articleComponents.length && (downloads.length || showRefSections) ? (
					<div className={css({ paddingBlock: "eds.dimension.16" })} />
				) : (
					""
				)}
				<ArticleContainer>
					<ArchivedVersionTable
						items={downloads}
						overrideDownloadUrl={overrideDownloadUrl}
						osName={osName}
						onOsNameChange={setOsName}
						archivedVersionsTitle={pageData?.archivedVersionsTitle}
					/>
				</ArticleContainer>
				{downloads.length && showRefSections ? <div className={css({ paddingBlock: "eds.dimension.48" })} /> : ""}
				{showRefSections
					? getSections(pageData?.refSections, sectionExtraProps, heroOwnsHeading(pageData?.hero))
					: null}
				{!showRefSections ? <div className={css({ paddingBlock: "eds.dimension.48" })} /> : ""}
			</div>
		</TemplateProviders>
	);
};
